import { forwardRef, useMemo } from 'react';
import {
  EmissionScope,
  type ActivityTypeReport,
  type EmissionSettings,
  type EmissionSummary,
  type EmissionsTarget,
  type IntensityMetrics,
  type MonthlyReport,
  type Scope2DualSummary,
} from '../../types/emissions';
import {
  type FormattedEmission,
  formatEmission,
  getDisplaySettingsFromEmissionSettings,
} from '../../utils/emissionsFormatter';
import { formatActivityType } from '../../config/activityTypeBranding';
import './one-pager.scss';

export type OnePagerMode = 'pdf' | 'image';

const ISO_CURRENCY_PATTERN = /^[A-Z]{3}$/;
const MONTH_LABELS = ['J', 'F', 'M', 'A', 'M', 'J', 'J', 'A', 'S', 'O', 'N', 'D'];
const MAX_HOTSPOTS = 3;
const FLAT_DELTA_THRESHOLD_PCT = 0.5;

const IntensityStat = ({
  label,
  formatted,
  unitSuffix,
}: {
  label: string;
  formatted: FormattedEmission;
  unitSuffix: string;
}) => (
  <div className='one-pager__stat'>
    <div className='one-pager__stat-label'>{label}</div>
    <div className='one-pager__stat-value'>
      {formatted.formatted}
      <span className='one-pager__stat-unit'>{formatted.unit} {unitSuffix}</span>
    </div>
  </div>
);

export interface EmissionsOnePagerPreviewProps {
  mode: OnePagerMode;
  initiativeName: string;
  initiativeLogo?: string;
  period: { start: string; end: string };
  summary: EmissionSummary;
  priorSummary?: EmissionSummary;
  hotspots?: ActivityTypeReport[];
  intensity?: IntensityMetrics;
  settings?: EmissionSettings;
  monthly?: MonthlyReport[];
  targets?: EmissionsTarget[];
  generatedAt: Date;
}

const formatMonthYear = (date: Date) => date.toLocaleDateString('en-GB', { month: 'short', year: 'numeric' });

const formatPeriodLabel = (start: string, end: string) =>
  `${formatMonthYear(new Date(start))} – ${formatMonthYear(new Date(end))}`;

const formatScopeLabel = (scope: string) => scope.replace(/^scope/i, 'Scope ');

// Validate at the boundary: if the persisted currency isn't a valid ISO 4217 code,
// fall back to USD rather than crashing the export on `Intl.NumberFormat`'s RangeError.
const formatCurrency = (value: number, currency: string): string => {
  const safe = ISO_CURRENCY_PATTERN.test(currency) ? currency : 'USD';
  return value.toLocaleString('en-US', { style: 'currency', currency: safe, maximumFractionDigits: 0 });
};

interface ScopeSlot {
  key: EmissionScope;
  label: string;
  colourKey: 1 | 2 | 3;
}

const SCOPES: ScopeSlot[] = [
  { key: EmissionScope.Scope1, label: 'Scope 1 · Direct', colourKey: 1 },
  { key: EmissionScope.Scope2, label: 'Scope 2 · Energy', colourKey: 2 },
  { key: EmissionScope.Scope3, label: 'Scope 3 · Value chain', colourKey: 3 },
];

type DeltaDirection = 'up' | 'down' | 'flat';
interface Delta {
  pct: number;
  direction: DeltaDirection;
}

const DELTA_META: Record<DeltaDirection, { verb: string; arrow: string }> = {
  down: { verb: 'decrease', arrow: '↓' },
  up: { verb: 'increase', arrow: '↑' },
  flat: { verb: 'no change', arrow: '' },
};

const calculateDelta = (current: number, prior: number): Delta => {
  const pct = ((current - prior) / prior) * 100;
  if (Math.abs(pct) < FLAT_DELTA_THRESHOLD_PCT) {
    return { pct: 0, direction: 'flat' };
  }
  return { pct: Math.abs(pct), direction: pct < 0 ? 'down' : 'up' };
};

// Missing months render as 0-height bars so the x-axis always spans Jan–Dec.
const buildMonthlyBars = (monthly: MonthlyReport[] | undefined): number[] => {
  const bars = Array<number>(12).fill(0);
  if (!monthly) {
    return bars;
  }
  for (const row of monthly) {
    const idx = row.month - 1;
    if (idx >= 0 && idx < 12) {
      bars[idx] += row.total;
    }
  }
  return bars;
};

const YoYTrend = ({
  bars,
  displayUnit,
}: {
  bars: number[];
  displayUnit: string;
}) => {
  const peak = Math.max(...bars, 0);
  if (peak === 0) {
    return null;
  }
  return (
    <div className='one-pager__trend' aria-label='Monthly emissions'>
      <div className='one-pager__trend-label'>Monthly emissions ({displayUnit})</div>
      <div className='one-pager__trend-bars'>
        {bars.map((v, i) => (
          <div key={i} className='one-pager__trend-bar-col'>
            <div
              className='one-pager__trend-bar'
              style={{ height: `${Math.max(2, (v / peak) * 100)}%` }}
            />
            <div className='one-pager__trend-bar-label'>{MONTH_LABELS[i]}</div>
          </div>
        ))}
      </div>
    </div>
  );
};

interface Highlight {
  key: string;
  label: string;
  body: string;
}

const countReportingMonths = (monthly: MonthlyReport[] | undefined): number => {
  if (!monthly) {
    return 0;
  }
  const months = new Set<number>();
  for (const row of monthly) {
    if (row.total > 0) {
      months.add(row.month);
    }
  }
  return months.size;
};

const pickLargestScope = (summary: EmissionSummary): { scope: EmissionScope; pct: number } | undefined => {
  if (summary.totalCO2e <= 0) {
    return undefined;
  }
  const ranked = [EmissionScope.Scope1, EmissionScope.Scope2, EmissionScope.Scope3]
    .map((scope) => ({ scope, value: summary.byScope[scope] ?? 0 }))
    .sort((a, b) => b.value - a.value);
  const top = ranked[0];
  if (top.value <= 0) {
    return undefined;
  }
  return { scope: top.scope, pct: (top.value / summary.totalCO2e) * 100 };
};

const buildHighlights = ({
  summary,
  priorSummary,
  monthly,
  delta,
  headlineTarget,
}: {
  summary: EmissionSummary;
  priorSummary?: EmissionSummary;
  monthly?: MonthlyReport[];
  delta?: Delta;
  headlineTarget?: EmissionsTarget;
}): Highlight[] => {
  const tiles: Highlight[] = [];

  // Reduction trajectory
  if (delta && priorSummary) {
    const magnitude = delta.direction === 'flat'
      ? '≈ flat'
      : `${delta.pct.toFixed(1)}% ${DELTA_META[delta.direction].verb}`;
    const targetNote = headlineTarget
      ? ` · tracking towards ${headlineTarget.reductionPercentage}% by ${headlineTarget.targetYear}`
      : '';
    tiles.push({
      key: 'trajectory',
      label: 'Reduction trajectory',
      body: `${magnitude} vs prior period${targetNote}.`,
    });
  } else {
    tiles.push({
      key: 'trajectory',
      label: 'Reduction trajectory',
      body: 'No prior period recorded yet — this is the baseline for future comparisons.',
    });
  }

  // Largest scope — fact only, no recommendations (the one-pager is
  // customer/investor-facing, so we keep it descriptive not prescriptive).
  const largest = pickLargestScope(summary);
  if (largest) {
    tiles.push({
      key: 'largest-scope',
      label: 'Largest scope',
      body: `${formatScopeLabel(largest.scope)} accounts for ${largest.pct.toFixed(0)}% of total emissions.`,
    });
  }

  // Data coverage
  const coveredScopes = [EmissionScope.Scope1, EmissionScope.Scope2, EmissionScope.Scope3]
    .filter((scope) => (summary.byScope[scope] ?? 0) > 0).length;
  const reportedMonths = countReportingMonths(monthly);
  const coverageParts = [`${coveredScopes} of 3 scopes covered`];
  if (reportedMonths > 0) {
    coverageParts.push(`${reportedMonths} of 12 months reported`);
  }
  coverageParts.push(`${summary.activityCount} activities`);
  tiles.push({
    key: 'coverage',
    label: 'Data coverage',
    body: `${coverageParts.join(' · ')}.`,
  });

  return tiles;
};

const KeyHighlights = ({ highlights }: { highlights: Highlight[] }) => (
  <section>
    <h2 className='one-pager__section-title'>Key highlights</h2>
    <div className='one-pager__highlights'>
      {highlights.map((h) => (
        <div key={h.key} className='one-pager__highlight'>
          <div className='one-pager__highlight-label'>{h.label}</div>
          <div className='one-pager__highlight-body'>{h.body}</div>
        </div>
      ))}
    </div>
  </section>
);

// Prefer the earliest active target — it's the nearest-term commitment.
const pickHeadlineTarget = (targets: EmissionsTarget[] | undefined): EmissionsTarget | undefined => {
  if (!targets || targets.length === 0) {
    return undefined;
  }
  const active = targets.filter((t) => t.status === 'active');
  const pool = active.length > 0 ? active : targets;
  return [...pool].sort((a, b) => a.targetYear - b.targetYear)[0];
};

const TargetsBlock = ({ target }: { target: EmissionsTarget }) => {
  const progress = Math.max(0, Math.min(100, target.progress ?? 0));
  return (
    <div className='one-pager__target'>
      <div className='one-pager__target-header'>
        <span className='one-pager__target-name'>{target.name}</span>
        <span className='one-pager__target-badge'>{target.framework.toUpperCase()}</span>
      </div>
      <div className='one-pager__target-headline'>
        −{target.reductionPercentage}% by {target.targetYear}{' '}
        <span className='one-pager__target-baseline'>(vs {target.baselineYear} baseline)</span>
      </div>
      <div className='one-pager__target-progress'>
        <div className='one-pager__target-progress-bar' style={{ width: `${progress}%` }} />
      </div>
      <div className='one-pager__target-progress-label'>{progress.toFixed(0)}% of reduction achieved</div>
    </div>
  );
};

const Scope2DualInfo = ({ dual, displaySettings }: {
  dual: Scope2DualSummary;
  displaySettings: ReturnType<typeof getDisplaySettingsFromEmissionSettings>;
}) => {
  const loc = formatEmission(dual.locationBased.total, displaySettings);
  const mkt = formatEmission(dual.marketBased.total, displaySettings);
  return (
    <div className='one-pager__scope-dual'>
      <span><b>L</b> {loc.formatted}</span>
      <span><b>M</b> {mkt.formatted}</span>
    </div>
  );
};

export const EmissionsOnePagerPreview = forwardRef<HTMLDivElement, EmissionsOnePagerPreviewProps>(
  (
    {
      mode,
      initiativeName,
      initiativeLogo,
      period,
      summary,
      priorSummary,
      hotspots,
      intensity,
      settings,
      monthly,
      targets,
      generatedAt,
    },
    ref
  ) => {
    const displaySettings = useMemo(
      () => getDisplaySettingsFromEmissionSettings(settings),
      [settings]
    );

    const totalKg = summary.totalCO2e;
    const totalFormatted = formatEmission(totalKg, displaySettings);

    const delta = useMemo<Delta | undefined>(() => {
      if (!priorSummary || priorSummary.totalCO2e <= 0) {
        return undefined;
      }
      return calculateDelta(summary.totalCO2e, priorSummary.totalCO2e);
    }, [summary, priorSummary]);

    const scopeValues = useMemo(
      () =>
        SCOPES.map((slot) => {
          const valueKg = summary.byScope[slot.key] ?? 0;
          return {
            ...slot,
            pct: totalKg > 0 ? (valueKg / totalKg) * 100 : 0,
            formatted: formatEmission(valueKg, displaySettings),
          };
        }),
      [summary, totalKg, displaySettings]
    );

    const topHotspots = useMemo(
      () => (hotspots ?? []).slice().sort((a, b) => b.total - a.total).slice(0, MAX_HOTSPOTS),
      [hotspots]
    );

    const monthlyBars = useMemo(() => buildMonthlyBars(monthly), [monthly]);
    const headlineTarget = useMemo(() => pickHeadlineTarget(targets), [targets]);
    const highlights = useMemo(
      () => buildHighlights({ summary, priorSummary, monthly, delta, headlineTarget }),
      [summary, priorSummary, monthly, delta, headlineTarget]
    );

    const hasIntensity = Boolean(
      intensity && (intensity.perEmployee != null || intensity.perRevenue != null)
    );

    return (
      <div ref={ref} className={`one-pager one-pager--${mode}`} data-testid='emissions-one-pager'>
        <header className='one-pager__header'>
          <div className='one-pager__brand'>
            {initiativeLogo ? (
              <img
                className='one-pager__logo'
                src={initiativeLogo}
                alt={`${initiativeName} logo`}
                crossOrigin='anonymous'
              />
            ) : null}
            <div>
              <h1 className='one-pager__name'>{initiativeName}</h1>
              <p className='one-pager__subtitle'>Carbon Footprint Summary</p>
            </div>
          </div>
          <div className='one-pager__period'>
            Reporting period
            <span className='one-pager__period-value'>
              {formatPeriodLabel(period.start, period.end)}
            </span>
          </div>
        </header>

        <section className='one-pager__headline'>
          <div className='one-pager__total'>
            <span className='one-pager__total-label'>Total emissions</span>
            <span className='one-pager__total-value'>
              {totalFormatted.formatted}
              <span className='one-pager__total-unit'>{totalFormatted.unit}</span>
            </span>
            {delta ? (
              <span className={`one-pager__delta one-pager__delta--${delta.direction}`}>
                {delta.direction === 'flat'
                  ? '≈ same as prior period'
                  : `${DELTA_META[delta.direction].arrow} ${delta.pct.toFixed(1)}% vs prior period`}
              </span>
            ) : null}
          </div>
          <YoYTrend bars={monthlyBars} displayUnit={totalFormatted.unit} />
        </section>

        <section className='one-pager__scopes'>
          {scopeValues.map((s) => (
            <div key={s.key} className={`one-pager__scope one-pager__scope--${s.colourKey}`}>
              <div className='one-pager__scope-label'>{s.label}</div>
              <div className='one-pager__scope-value'>{s.formatted.display}</div>
              <div className='one-pager__scope-meta'>{s.pct.toFixed(1)}% of total</div>
              <div className='one-pager__scope-bar'>
                <div
                  className='one-pager__scope-bar-fill'
                  style={{ width: `${Math.max(2, Math.min(100, s.pct))}%` }}
                />
              </div>
              {s.key === EmissionScope.Scope2 && summary.scope2DualReporting ? (
                <Scope2DualInfo dual={summary.scope2DualReporting} displaySettings={displaySettings} />
              ) : null}
            </div>
          ))}
        </section>

        {hasIntensity && intensity ? (
          <section>
            <h2 className='one-pager__section-title'>Carbon intensity</h2>
            <div className='one-pager__stats'>
              {intensity.perEmployee != null ? (
                <IntensityStat
                  label='Per employee'
                  formatted={formatEmission(intensity.perEmployee, displaySettings)}
                  unitSuffix='/ FTE'
                />
              ) : null}
              {intensity.perRevenue != null ? (
                <IntensityStat
                  label='Per revenue'
                  formatted={formatEmission(intensity.perRevenue * 1_000_000, displaySettings)}
                  unitSuffix={`/ ${formatCurrency(1_000_000, intensity.businessMetrics.revenueCurrency ?? 'USD')}`}
                />
              ) : null}
            </div>
          </section>
        ) : null}

        {headlineTarget ? (
          <section>
            <h2 className='one-pager__section-title'>Reduction commitment</h2>
            <TargetsBlock target={headlineTarget} />
          </section>
        ) : null}

        {topHotspots.length > 0 ? (
          <section>
            <h2 className='one-pager__section-title'>Top emission hotspots</h2>
            <div className='one-pager__hotspots'>
              {topHotspots.map((h, i) => (
                <div key={`${h.scope}-${h.activityType}`} className='one-pager__hotspot'>
                  <div className='one-pager__hotspot-rank'>#{i + 1} · {formatScopeLabel(h.scope)}</div>
                  <div className='one-pager__hotspot-name'>{formatActivityType(h.activityType)}</div>
                  <div className='one-pager__hotspot-value'>
                    {formatEmission(h.total, displaySettings).display}
                  </div>
                </div>
              ))}
            </div>
          </section>
        ) : null}

        <KeyHighlights highlights={highlights} />

        {settings ? (
          <div className='one-pager__methodology'>
            Methodology: GHG Protocol Corporate Standard · {settings.gwpMethodology} GWP factors ·{' '}
            {settings.organizationalBoundary.replace(/_/g, ' ')} boundary · base year{' '}
            {settings.baseYear} · default region {settings.defaultRegion} · calculated{' '}
            {generatedAt.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}.
          </div>
        ) : null}

        <footer className='one-pager__footer'>
          <span>
            Measured with <span className='one-pager__footer-brand'>G17 Emissions Tracker</span>
          </span>
          <span>g17.eco</span>
        </footer>
      </div>
    );
  }
);

EmissionsOnePagerPreview.displayName = 'EmissionsOnePagerPreview';
